// src/pages/users/UserFormSidePanelContent.tsx
import React, { useState, useEffect, useMemo } from 'react';
import SaveButton from '../../components/common/SaveButton';
import CloseButtonSidePanel from '../../components/common/CloseButtonSidePanel';
import MultiSelectList from '../../components/common/MultiSelectList';
import Tabs, { TabItem } from '../../components/common/Tabs';
import { Pessoa, LojaAcesso, PermissionsMap } from '../../types/usuarios/Pessoa';
import { UsuarioParametrosAprovacaoUpsert } from '../../types/aprovacao/Aprovacao';
import { formatCpf } from '../../utils/formatUtils';
import { FormattedFields, ValidationErrors } from '../../hooks/useFormFormatting';
import { apiGet } from '../../services/api';
import { useEntitlement } from '../../contexts/EntitlementContext';
import ApproverParamsSection from './ApproverParamsSection';
import './UserFormSidePanelContent.css';
import { logger } from '@/utils/logger';

// Interface para metadados de permissões
interface PermissionMetadata {
  lojas_acesso: string[];
  requisicoes: string[];
  contatos: string[];
  modulo: string[];
  usuarios: string[];
  pessoas_connection: string[];
  gestao_compras: string[];
  gestao_conferencias: string[];
  configuration: string[];
  bluebi: string[];
}

interface UserFormSidePanelContentProps {
  selectedPerson: Pessoa | null;
  fields: FormattedFields;
  errors: ValidationErrors;
  lojasAcesso: LojaAcesso[];
  selectedLojasIds: number[];
  isLoading: boolean;
  handleChange: (field: keyof FormattedFields, value: string) => void;
  handleBlur: (field: keyof FormattedFields) => void;
  handleLojaChange: (lojaId: number) => void;
  handleSave: (permissions?: PermissionsMap, approverParams?: UsuarioParametrosAprovacaoUpsert | null) => void;
  handleCancel: () => void;
  isFormValid: () => boolean;
  theme: 'dark' | 'light';
  podeEditarParametrosAprovacao: boolean;
}

// Granular = { module dentro de PermissionsMap, perm string completa (ex.: 'gestao_compras.criar_cotacoes') }
interface GranularRef {
  module: keyof PermissionsMap;
  perm: string;
}

// Configuracao de cada aba de modulo: master (chave dentro de permissions.modulo) +
// fontes de granulares no PermissionMetadata.
interface ModuleTabConfig {
  id: string;
  label: string;
  masterKey: string | null;
  granularSources: { module: keyof PermissionsMap; metaKey: keyof PermissionMetadata }[];
  extraContent?: 'approverParams';
}

// Ordem alfabetica fixa das abas.
const MODULE_TABS: ModuleTabConfig[] = [
  {
    id: 'bluebi',
    label: 'Blue B.I.',
    masterKey: 'bluebi',
    granularSources: [{ module: 'bluebi', metaKey: 'bluebi' }],
  },
  { id: 'contrato_bluelt', label: 'Bluelt', masterKey: 'contrato_bluelt', granularSources: [] },
  {
    id: 'gestao_compras',
    label: 'Compras',
    masterKey: 'gestao_compras',
    granularSources: [
      { module: 'gestao_compras', metaKey: 'gestao_compras' },
      { module: 'pessoas_connection', metaKey: 'pessoas_connection' },
    ],
  },
  {
    id: 'gestao_conferencias',
    label: 'Conferências',
    masterKey: 'gestao_conferencias',
    granularSources: [{ module: 'gestao_conferencias', metaKey: 'gestao_conferencias' }],
  },
  {
    id: 'gestao_transferencias',
    label: 'Transferências',
    masterKey: 'gestao_transferencias',
    granularSources: [],
  },
  {
    id: 'gestao_usuarios',
    label: 'Usuários',
    masterKey: 'gestao_usuarios',
    granularSources: [{ module: 'usuarios', metaKey: 'usuarios' }],
  },
  { id: 'integracoes', label: 'Integrações', masterKey: 'integracoes', granularSources: [] },
  {
    id: 'parametrizacao',
    label: 'Parametrização',
    masterKey: 'parametrizacao',
    granularSources: [
      { module: 'lojas_acesso', metaKey: 'lojas_acesso' },
      { module: 'configuration', metaKey: 'configuration' },
    ],
  },
  { id: 'planejamento', label: 'Planejamento', masterKey: 'planejamento', granularSources: [] },
  { id: 'relatorios', label: 'Relatórios', masterKey: 'relatorios', granularSources: [] },
  {
    id: 'requisicoes',
    label: 'Requisições',
    masterKey: 'requisicoes',
    granularSources: [
      { module: 'requisicoes', metaKey: 'requisicoes' },
      { module: 'contatos', metaKey: 'contatos' },
    ],
    extraContent: 'approverParams',
  },
];

// Nomes amigaveis para permissoes granulares.
const permissionLabels: { [key: string]: string } = {
  'lojas_acesso.cadastrar': 'Cadastrar Lojas',
  'lojas_acesso.listar': 'Listar Lojas',
  'lojas_acesso.editar': 'Editar Lojas',
  'configuration.listar': 'Listar regras de aprovação e logo de e-mail',
  'configuration.editar': 'Editar regras de aprovação e logo de e-mail',
  'configuration.listar_login': 'Listar método de login',
  'configuration.editar_login': 'Editar método de login',
  'contatos.listar': 'Listar Contatos',
  'requisicoes.listar': 'Listar Requisições',
  'requisicoes.criar': 'Criar Requisições',
  'requisicoes.aprovar_requisicao_compra': 'Aprovar Requisições de Compra',
  'usuarios.listar': 'Listar Usuários',
  'usuarios.cadastrar': 'Cadastrar Usuários',
  'usuarios.editar': 'Editar Usuários',
  'usuarios.editar_parametros_aprovacao': 'Editar parâmetros de aprovação',
  'pessoas_connection.listar': 'Listar Clientes e Fornecedores',
  'gestao_compras.listar': 'Listar Requisições, Cotações e Ordens de Compra',
  'gestao_compras.devolver_itens': 'Devolver Itens de Requisições Pendentes',
  'gestao_compras.criar_cotacoes': 'Criar nova Cotação',
  'gestao_compras.devolver_itens_cotacoes': 'Devolver Itens da Cotação',
  'gestao_compras.cancelar_cotacoes': 'Cancelar Cotação',
  'gestao_compras.criar_ordem_compra': 'Criar Ordens de Compra',
  'gestao_compras.cancelar_ordem_compra': 'Cancelar Ordem de Compra Aberta',
  'gestao_conferencias.listar_conferencias': 'Listar Conferências',
  'gestao_conferencias.finalizar_conferencias': 'Finalizar Conferências',
  'gestao_conferencias.finalizar_ampla_conferencias': 'Finalizar Conferências de Outros Usuários',
  'gestao_conferencias.reabrir_conferencias': 'Reabrir Conferências',
  'gestao_conferencias.reabrir_ampla_conferencias': 'Reabrir Conferências de Outros Usuários',
  'gestao_conferencias.editar_qtd_recebida_conferencias': 'Editar Qtd Recebida em Conferências',
  'gestao_conferencias.editar_qtd_recebida_ampla_conferencias': 'Editar Qtd Recebida de Outros Usuários',
  'gestao_conferencias.criar_conferencias': 'Criar Conferências',
  'gestao_conferencias.visualizar_ampla_conferencias': 'Visualizar Conferências de Outros Usuários',
  'gestao_conferencias.deletar_conferencias': 'Deletar Conferências',
  'gestao_conferencias.deletar_ampla_conferencias': 'Deletar Conferências de Outros Usuários',
  'bluebi.visualizar': 'Visualizar Painéis de B.I.',
  'bluebi.editar': 'Cadastrar Painéis e Conceder Acesso',
};

const UserFormSidePanelContent: React.FC<UserFormSidePanelContentProps> = ({
  selectedPerson,
  fields,
  errors,
  lojasAcesso,
  selectedLojasIds,
  isLoading,
  handleChange,
  handleBlur,
  handleLojaChange,
  handleSave,
  handleCancel,
  isFormValid,
  theme,
  podeEditarParametrosAprovacao,
}) => {
  const [permissionMetadata, setPermissionMetadata] = useState<PermissionMetadata | null>(null);
  const [permissions, setPermissions] = useState<PermissionsMap>({});
  const [approverParams, setApproverParams] = useState<UsuarioParametrosAprovacaoUpsert | null>(null);
  // Gate de módulo (sem bypass de MASTER): esconde abas de áreas cujo módulo de
  // tenant não está contratado. NÃO toca no estado `permissions` — o payload
  // continua completo, preservando valores existentes (backend faz REPLACE).
  const { isAreaEnabled } = useEntitlement();

  // Inicializa metadados de permissões
  useEffect(() => {
    const fetchPermissionsMetadata = async () => {
      try {
        const response = await apiGet('/v1/permissoes/metadata');
        const metadata: PermissionMetadata = response.data;
        setPermissionMetadata(metadata);

        // Inicializa todas as permissões como false
        const initialPermissions: PermissionsMap = {};
        Object.entries(metadata).forEach(([module, perms]: [string, string[]]) => {
          initialPermissions[module as keyof PermissionsMap] = perms.reduce(
            (acc: { [key: string]: boolean }, perm: string) => ({
              ...acc,
              [perm.replace(`${module}.`, '')]: false,
            }),
            {}
          );
        });

        // Carrega permissões do usuário, se disponíveis, mas apenas as válidas
        if (selectedPerson?.permissoes) {
          Object.entries(selectedPerson.permissoes).forEach(([module, perms]: [string, { [key: string]: boolean }]) => {
            if (module in initialPermissions) {
              const validPerms = metadata[module as keyof PermissionMetadata] || [];
              const filteredPerms = Object.keys(perms).reduce(
                (acc: { [key: string]: boolean }, perm: string) => {
                  if (validPerms.includes(`${module}.${perm}`)) {
                    acc[perm] = perms[perm];
                  }
                  return acc;
                },
                {}
              );
              initialPermissions[module as keyof PermissionsMap] = {
                ...initialPermissions[module as keyof PermissionsMap],
                ...filteredPerms,
              };
            }
          });
        }

        setPermissions(initialPermissions);
      } catch (error) {
        logger.error('Erro ao carregar metadados de permissões:', error);
      }
    };
    fetchPermissionsMetadata();
  }, [selectedPerson]);

  // Resolve os granulares ativos de uma aba (aplica filtro _ampla_ por role
  // em gestao_conferencias).
  const resolveGranulars = (tab: ModuleTabConfig): GranularRef[] => {
    if (!permissionMetadata) return [];
    const out: GranularRef[] = [];
    tab.granularSources.forEach(({ module, metaKey }) => {
      const perms = permissionMetadata[metaKey] || [];
      perms.forEach((perm) => {
        if (
          module === 'gestao_conferencias' &&
          perm.includes('_ampla_conferencias') &&
          fields.role !== 'ROLE_USER'
        ) {
          return;
        }
        out.push({ module, perm });
      });
    });
    return out;
  };

  // Toggle de granular: aplica regra master ↔ granular.
  //  - marcar granular ⇒ marca master se ainda nao estiver
  //  - desmarcar ultimo granular ⇒ desmarca master
  const toggleGranular = (tab: ModuleTabConfig, ref: GranularRef) => {
    setPermissions((prev) => {
      const granularKey = ref.perm.replace(`${ref.module}.`, '');
      const wasChecked = prev[ref.module]?.[granularKey] ?? false;
      const updatedModule = { ...prev[ref.module], [granularKey]: !wasChecked };
      const next: PermissionsMap = { ...prev, [ref.module]: updatedModule };

      if (tab.masterKey) {
        const granulars = resolveGranulars(tab);
        const anyChecked = granulars.some((g) => {
          const k = g.perm.replace(`${g.module}.`, '');
          if (g.module === ref.module && k === granularKey) return !wasChecked;
          return next[g.module]?.[k] ?? false;
        });
        const moduloUpdated = { ...next.modulo, [tab.masterKey]: anyChecked };
        next.modulo = moduloUpdated;
      }

      return next;
    });
  };

  // Toggle do master: liga/desliga modulo + todos os granulares da aba.
  const toggleMaster = (tab: ModuleTabConfig) => {
    if (!tab.masterKey) return;
    setPermissions((prev) => {
      const currentMaster = prev.modulo?.[tab.masterKey!] ?? false;
      const newValue = !currentMaster;
      const moduloUpdated = { ...prev.modulo, [tab.masterKey!]: newValue };
      const next: PermissionsMap = { ...prev, modulo: moduloUpdated };

      const granulars = resolveGranulars(tab);
      granulars.forEach((g) => {
        const k = g.perm.replace(`${g.module}.`, '');
        next[g.module] = { ...next[g.module], [k]: newValue };
      });

      return next;
    });
  };

  // Envio do formulário com permissões + parametros de aprovacao (2-step PUT).
  const onSave = () => {
    handleSave(permissions, approverParams);
  };

  const isMaster = selectedPerson?.usuario?.role === 'ROLE_MASTER';

  // Monta items das Tabs (1 por modulo, ordem alfabetica fixa).
  const tabItems = useMemo<TabItem[]>(() => {
    if (!permissionMetadata) return [];
    // Esconde a aba inteira quando o módulo de tenant da área (tab.id) não está
    // contratado. tab.id ∈ {requisicoes, gestao_compras, gestao_conferencias} é
    // gateado por PO; demais ids não mapeiam módulo → sempre visíveis.
    return MODULE_TABS.filter((tab) => isAreaEnabled(tab.id)).map((tab) => {
      const granulars = resolveGranulars(tab);
      const masterChecked = tab.masterKey
        ? permissions.modulo?.[tab.masterKey] ?? false
        : false;

      const checkedCount = granulars.reduce((acc, g) => {
        const k = g.perm.replace(`${g.module}.`, '');
        return acc + (permissions[g.module]?.[k] ? 1 : 0);
      }, 0);
      const badge = granulars.length > 0 ? `${checkedCount}/${granulars.length}` : undefined;

      const content = (
        <>
          {tab.masterKey && (
            <label className="permission-master-row">
              <input
                type="checkbox"
                checked={masterChecked}
                onChange={() => toggleMaster(tab)}
              />
              <strong>Habilitar {tab.label}</strong>
            </label>
          )}
          {granulars.length > 0 && (
            <div className="form-grid-permission">
              {granulars.map((g) => {
                const granularKey = g.perm.replace(`${g.module}.`, '');
                const checked = permissions[g.module]?.[granularKey] ?? false;
                const isGranularParamsAprov = g.perm === 'usuarios.editar_parametros_aprovacao';
                const checkboxDisabled = isGranularParamsAprov && !podeEditarParametrosAprovacao;
                return (
                  <div key={g.perm} className="label-permission">
                    <div className="form-permission">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleGranular(tab, g)}
                        disabled={checkboxDisabled}
                      />
                    </div>
                    <div>{permissionLabels[g.perm] || g.perm}</div>
                  </div>
                );
              })}
            </div>
          )}
          {tab.extraContent === 'approverParams' && (
            <ApproverParamsSection
              usuarioId={selectedPerson?.id ?? null}
              theme={theme}
              disabled={isLoading}
              podeEditar={podeEditarParametrosAprovacao}
              onChange={setApproverParams}
            />
          )}
        </>
      );

      return { id: tab.id, label: tab.label, badge, content };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionMetadata, permissions, fields.role, podeEditarParametrosAprovacao, theme, isLoading, selectedPerson, isAreaEnabled]);

  return (
    <div className={`form-content ${theme}`}>
      <CloseButtonSidePanel onClick={handleCancel} className="close-button-absolute" disabled={isLoading}/>
      <div className="facelet-h3">
      <h3>
        {selectedPerson?.id ? (
          <>Visualizando: Editar usuário</>
        ) : (
          <>Visualizando: Criar novo usuário</>
        )}
      </h3>
      </div>
      <hr />
      <div className="form-grid">
        <label>
          Código do Usuário (Integração):
          <input
            type="text"
            style={{ backgroundColor: '#e74d3c2f' }}
            value={fields.codUsuarioIntegracao || ''}
            disabled
            className={errors.codUsuarioIntegracao ? 'input-error' : ''}
          />
          {errors.codUsuarioIntegracao && <span className="error-message">{errors.codUsuarioIntegracao}</span>}
        </label>
        <label>
          CPF (Integração):
          <input
            type="text"
            style={{ backgroundColor: '#e74d3c2f' }}
            value={formatCpf(fields.cpf || '')}
            disabled
            className={errors.cpf ? 'input-error' : ''}
          />
          {errors.cpf && <span className="error-message">{errors.cpf}</span>}
        </label>
        <label>
          userName:
          <input
            type="text"
            value={fields.userName}
            onChange={(e) => handleChange('userName', e.target.value)}
            disabled={isMaster}
            style={isMaster ? { backgroundColor: '#e74d3c2f' } : undefined}
            className={errors.userName ? 'input-error' : ''}
          />
          {errors.userName && <span className="error-message">{errors.userName}</span>}
        </label>
        <label>
          Nome:
          <input
            type="text"
            value={fields.nome}
            onChange={(e) => handleChange('nome', e.target.value)}
            disabled={isMaster}
            style={isMaster ? { backgroundColor: '#e74d3c2f' } : undefined}
            className={errors.nome ? 'input-error' : ''}
          />
          {errors.nome && <span className="error-message">{errors.nome}</span>}
        </label>
        <label>
          WhatsApp:
          <input
            type="text"
            value={fields.whatsapp}
            onChange={(e) => handleChange('whatsapp', e.target.value)}
            disabled={isMaster}
            style={isMaster ? { backgroundColor: '#e74d3c2f' } : undefined}
            className={errors.whatsapp ? 'input-error' : ''}
          />
          {errors.whatsapp && <span className="error-message">{errors.whatsapp}</span>}
        </label>
        <label>
          Email:
          <input
            type="email"
            value={fields.email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            disabled={isMaster}
            style={isMaster ? { backgroundColor: '#e74d3c2f' } : undefined}
            className={errors.email ? 'input-error' : ''}
          />
          {errors.email && <span className="error-message">{errors.email}</span>}
        </label>
        <MultiSelectList
          items={lojasAcesso}
          selectedIds={selectedLojasIds}
          handleChange={handleLojaChange}
          theme={theme}
          label="Lojas de Acesso"
          displayFields={[
            { key: 'codFilial', label: 'Código de Filial' },
            { key: 'nomeFantasia', label: 'Nome Fantasia' },
          ]}
          disabled={isMaster}
        />
        <label>
          Perfil:
          <select
            value={fields.role}
            onChange={(e) => handleChange('role', e.target.value)}
            disabled={isMaster}
            style={isMaster ? { backgroundColor: '#e74d3c2f' } : undefined}
            className={errors.role ? 'input-error' : 'role-select'}
          >
            <option value="ROLE_USER">Usuário</option>
            <option value="ROLE_ADMIN">Administrador</option>
          </select>
          {errors.role && <span className="error-message">{errors.role}</span>}
        </label>
      </div>
      <div className="form-section">
        <div className="permission-content">
          {isMaster ? (
            <div className="permission-message">
              Este usuário tem todas as permissões do sistema. Para fazer alterações nesse usuário contate o suporte.
            </div>
          ) : (
            permissionMetadata && (
              <Tabs
                items={tabItems}
                theme={theme}
                ariaLabel="Permissões do Usuário por módulo"
                className="permission-tabs"
              />
            )
          )}
        </div>
      </div>
      <div className="form-actions">
        <SaveButton
          isLoading={isLoading}
          onClick={onSave}
          disabled={!isFormValid()}
        />
      </div>
    </div>
  );
};

export default UserFormSidePanelContent;
